// speech.ts — ElevenLabs TTS module

import { getAudioBlob } from '../lib/audio-storage';
import { store } from '../store';

const FEMALE_VOICE_ID = 'DXFkLCBUTmvXpp2QwZjA';
const MALE_VOICE_ID = 'UaYTS0wayjmO9KD1LR4R';
const MODEL_ID = 'eleven_turbo_v2_5';

const TTS_CACHE_DB = 'actingapp-audio';
const TTS_CACHE_STORE = 'tts-cache';

let activeAudio: HTMLAudioElement | null = null;
let userHasInteracted = false;

function _openTtsDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(TTS_CACHE_DB, 2);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains('clips')) {
        db.createObjectStore('clips');
      }
      if (!db.objectStoreNames.contains(TTS_CACHE_STORE)) {
        db.createObjectStore(TTS_CACHE_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function _getCached(key: string): Promise<Blob | null> {
  const db = await _openTtsDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TTS_CACHE_STORE, 'readonly');
    const r = tx.objectStore(TTS_CACHE_STORE).get(key);
    r.onsuccess = () => resolve(r.result || null);
    r.onerror = () => reject(r.error);
  });
}

async function _putCached(key: string, blob: Blob): Promise<void> {
  const db = await _openTtsDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(TTS_CACHE_STORE, 'readwrite');
    tx.objectStore(TTS_CACHE_STORE).put(blob, key);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

function _currentVoiceId(): string {
  const { selectedVoice } = store.getState().settings;
  return selectedVoice === 'male' ? MALE_VOICE_ID : FEMALE_VOICE_ID;
}

function _playBlob(blob: Blob, callback: (() => void) | null) {
  const url = URL.createObjectURL(blob);
  const audio = new Audio(url);
  activeAudio = audio;
  const done = () => {
    URL.revokeObjectURL(url);
    activeAudio = null;
    const { isPracticing } = store.getState().practice;
    if (!isPracticing) return;
    if (callback) callback();
  };
  audio.onended = done;
  audio.onerror = done;
  audio.play();
}

async function _speakViaElevenLabs(text: string, callback: (() => void) | null) {
  const voiceId = _currentVoiceId();
  const cacheKey = voiceId + '|' + text;

  const cached = await _getCached(cacheKey).catch(() => null);
  if (cached) {
    _playBlob(cached, callback);
    return;
  }

  const response = await fetch('/api/speak', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ text, voiceId, modelId: MODEL_ID }),
  });

  if (!response.ok) {
    console.error('ElevenLabs proxy error', response.status);
    if (callback) callback();
    return;
  }

  const blob = await response.blob();
  _putCached(cacheKey, blob).catch(() => {});
  _playBlob(blob, callback);
}

export function initializeAudioContext() {
  userHasInteracted = true;
  // Play silent audio to unlock iOS audio
  const silent = new Audio();
  silent.src = 'data:audio/mp3;base64,SUQzBAAAAAAAI1RTU0UAAAAPAAADTGF2ZjU4Ljc2LjEwMAAAAAAAAAAAAAAA//tQAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAAWGluZwAAAA8AAAACAAADhAC7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7u7//////////////////////////////////////////////////////////////////8AAAAATGF2YzU4LjEzAAAAAAAAAAAAAAAAJAAAAAAAAAAAA4T/////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////';
  silent.play().catch(() => {});
}

export function stopSpeech() {
  if (activeAudio) {
    try { activeAudio.pause(); } catch (e) {}
    activeAudio = null;
  }
}

export function loadVoices() {}

export function speakFeedback(text: string) {
  _speakViaElevenLabs(text, null).catch(err => console.error('speakFeedback error:', err));
}

export async function speakLine(text: string, callback: (() => void) | null, recordedAudioId?: string) {
  if (!userHasInteracted) {
    console.warn('Audio not initialized. Call initializeAudioContext() first.');
    return;
  }

  stopSpeech();

  const { useHumanRecordings } = store.getState().script;
  if (useHumanRecordings && recordedAudioId) {
    const blob = await getAudioBlob(recordedAudioId).catch(() => null);
    if (blob) {
      _playBlob(blob, callback);
      return;
    }
  }

  await _speakViaElevenLabs(text, callback);
}
